import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Actions.css";

interface ActionCard {
  name: string;
  image: string;
  description: string;
}

const actionCards: ActionCard[] = [
  {
    name: "Backstab",
    image: "abilities/backstab.webp",
    description: "Strike from the shadows for devastating damage",
  },
  {
    name: "Grappling Hook",
    image: "abilities/grapplinghook.webp",
    description: "Traverse the battlefield and reposition strategically",
  },
  {
    name: "Incinerating Blast",
    image: "abilities/incineratingblast.webp",
    description: "Unleash a powerful fire spell to devastate enemies",
  },
  {
    name: "Infernal Javelin",
    image: "abilities/infernal javelin.webp",
    description: "Hurl a blazing spear across the battlefield",
  },
  {
    name: "Liminal Recall",
    image: "abilities/liminalrecall.webp",
    description: "Manipulate time and space to your advantage",
  },
  {
    name: "Shadow Stithe",
    image: "abilities/shadowstithe.webp",
    description: "Weave darkness to bind and control your foes",
  },
];

const Actions: React.FC = () => {
  const [selectedCard, setSelectedCard] = useState<ActionCard | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <div id="tactics" className="actions-subsection" ref={ref}>
      <div className="container">
        <motion.div
          className="actions-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="actions-header" variants={itemVariants}>
            <h3>Use Your Tactics to Defeat Your Enemies</h3>
            <p>
              Your Tactics are your primary tools for dealing damage, applying
              effects, and influencing the battlefield. Action cards can only be
              played on your turn, while Instants can be played at any time,
              even in response to an opponent's move, creating layers of depth
              in timing and resource management. All cards resolve using a stack
              system, where each new card is placed on top and the last one
              played resolves first, allowing for fast-paced strategic chains,
              bluffs, and counterplays.
            </p>
          </motion.div>

          {/* Featured Abilities */}
          <motion.div className="actions-grid" variants={itemVariants}>
            {actionCards.map((card, index) => (
              <motion.div
                key={card.name}
                className="action-card"
                variants={cardVariants}
                whileHover={{
                  scale: 1.05,
                  y: -10,
                  transition: { duration: 0.2 },
                }}
                onClick={() => setSelectedCard(card)}
                style={{ cursor: "pointer" }}
              >
                <div className="action-image">
                  <img src={card.image} alt={card.name} loading="lazy" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Card Modal */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            className="card-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              className="card-modal"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close-btn"
                onClick={() => setSelectedCard(null)}
              >
                ×
              </button>
              <img src={selectedCard.image} alt={selectedCard.name} />
              <div className="modal-info">
                {/* <h3>{selectedCard.name}</h3> */}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Actions;
